import { useEffect, useState } from "react";
import { Save, ChevronRight, Image as ImageIcon, Blocks, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

import { API } from "@/apiConfig";
import ContentPublicPreview, { announceContentSaved } from "@/components/ContentPublicPreview";

const PAGE_FIELDS = {
  accueil: [
    { key: "_heading_hero", label: "── Hero", type: "heading" },
    { key: "hero_title", label: "Titre principal (Hero)", type: "text" },
    { key: "hero_subtitle", label: "Sous-titre (Hero)", type: "textarea" },
    { key: "hero_kicker", label: "Sur-titre (Hero)", type: "text" },
    { key: "hero_image", label: "Photo hero", type: "image" },
    { key: "hero_image_alt", label: "Texte alternatif photo hero", type: "text" },
    { key: "primary_cta_label", label: "Bouton principal", type: "text" },
    { key: "secondary_cta_label", label: "Bouton secondaire", type: "text" },
    { key: "_heading_services", label: "── Services accueil", type: "heading" },
    { key: "services_title", label: "Titre section services", type: "text" },
    { key: "services_desc", label: "Description section services", type: "textarea" },
    ...[1, 2, 3, 4, 5, 6].flatMap(n => [
      { key: `service${n}_title`, label: `Service ${n} — Titre`, type: "text" },
      { key: `service${n}_desc`, label: `Service ${n} — Description`, type: "textarea", rows: 2 },
    ]),
    { key: "_heading_stats", label: "── Statistiques", type: "heading" },
    { key: "stat1_num", label: "Stat 1 — Chiffre (ex: 10+)", type: "text" },
    { key: "stat1_label", label: "Stat 1 — Libellé", type: "text" },
    { key: "stat2_num", label: "Stat 2 — Chiffre (ex: 200+)", type: "text" },
    { key: "stat2_label", label: "Stat 2 — Libellé", type: "text" },
    { key: "stat3_num", label: "Stat 3 — Chiffre (ex: 95%)", type: "text" },
    { key: "stat3_label", label: "Stat 3 — Libellé", type: "text" },
    { key: "_heading_about", label: "── Bloc à propos", type: "heading" },
    { key: "about_image", label: "Photo bloc à propos", type: "image" },
    { key: "about_image_alt", label: "Texte alternatif photo à propos", type: "text" },
    { key: "about_kicker", label: "Sur-titre bloc à propos", type: "text" },
    { key: "about_title", label: "Titre bloc à propos", type: "text" },
    { key: "about_text1", label: "Paragraphe 1", type: "textarea" },
    { key: "about_text2", label: "Paragraphe 2", type: "textarea" },
    { key: "about_button_label", label: "Bouton bloc à propos", type: "text" },
    { key: "about_quote", label: "Citation sur photo", type: "textarea", rows: 2 },
    { key: "about_quote_author", label: "Auteur citation", type: "text" },
    { key: "_heading_faq", label: "── FAQ accueil", type: "heading" },
    { key: "faq_kicker", label: "Sur-titre FAQ", type: "text" },
    { key: "faq_title", label: "Titre FAQ", type: "text" },
    { key: "faq_desc", label: "Texte FAQ", type: "textarea" },
    { key: "faq_button_label", label: "Bouton FAQ", type: "text" },
    { key: "_heading_cta", label: "── Appel à l'action (bas de page)", type: "heading" },
    { key: "cta_title", label: "Titre CTA final", type: "text" },
    { key: "cta_subtitle", label: "Sous-titre CTA", type: "text" },
    { key: "cta_primary_label", label: "Bouton CTA principal", type: "text" },
    { key: "cta_secondary_label", label: "Bouton CTA secondaire", type: "text" },
  ],
  "sophrologie-caycedienne": [
    { key: "_heading_header", label: "── En-tête", type: "heading" },
    { key: "page_title", label: "Titre de la page", type: "text" },
    { key: "intro_text", label: "Texte d'introduction", type: "textarea" },
    { key: "hero_kicker", label: "Sur-titre", type: "text" },
    { key: "_heading_intro", label: "── Bloc image & introduction", type: "heading" },
    { key: "main_image", label: "Photo principale", type: "image" },
    { key: "main_image_alt", label: "Texte alternatif photo principale", type: "text" },
    { key: "section_title", label: "Titre section", type: "text" },
    { key: "description", label: "Description principale", type: "textarea" },
    { key: "description_2", label: "Paragraphe complémentaire 1", type: "textarea" },
    { key: "description_3", label: "Paragraphe complémentaire 2", type: "textarea" },
    { key: "_heading_piliers", label: "── Piliers", type: "heading" },
    { key: "piliers_kicker", label: "Sur-titre piliers", type: "text" },
    { key: "piliers_title", label: "Titre piliers", type: "text" },
    ...[1, 2, 3, 4].flatMap(n => [
      { key: `pilier${n}_title`, label: `Pilier ${n} — Titre`, type: "text" },
      { key: `pilier${n}_desc`, label: `Pilier ${n} — Description`, type: "textarea", rows: 2 },
    ]),
    { key: "_heading_benefits", label: "── Bénéfices", type: "heading" },
    { key: "benefits_title", label: "Titre bénéfices", type: "text" },
    { key: "benefits_desc", label: "Texte bénéfices", type: "textarea" },
    { key: "benefits_list", label: "Bénéfices — un par ligne", type: "textarea", rows: 5 },
    { key: "benefits_button_label", label: "Bouton bénéfices", type: "text" },
  ],
  "a-propos": [
    { key: "_heading_intro", label: "── Introduction", type: "heading" },
    { key: "bio_title", label: "Titre biographie", type: "text" },
    { key: "bio_text1", label: "Paragraphe 1", type: "textarea" },
    { key: "bio_text2", label: "Paragraphe 2", type: "textarea" },
    { key: "bio_text3", label: "Paragraphe 3", type: "textarea" },
    { key: "portrait_image", label: "Photo portrait / cabinet", type: "image" },
    { key: "portrait_image_alt", label: "Texte alternatif photo", type: "text" },
    { key: "_heading_formation", label: "── Parcours & formations", type: "heading" },
    { key: "form1_title", label: "Formation 1 — Intitulé", type: "text" },
    { key: "form1_org", label: "Formation 1 — Organisme", type: "text" },
    { key: "form1_year", label: "Formation 1 — Année", type: "text" },
    { key: "form2_title", label: "Formation 2 — Intitulé", type: "text" },
    { key: "form2_org", label: "Formation 2 — Organisme", type: "text" },
    { key: "form2_year", label: "Formation 2 — Année", type: "text" },
    { key: "form3_title", label: "Formation 3 — Intitulé", type: "text" },
    { key: "form3_org", label: "Formation 3 — Organisme", type: "text" },
    { key: "form3_year", label: "Formation 3 — Année", type: "text" },
    { key: "form4_title", label: "Formation 4 — Intitulé", type: "text" },
    { key: "form4_org", label: "Formation 4 — Organisme", type: "text" },
    { key: "form4_year", label: "Formation 4 — Année", type: "text" },
    { key: "form5_title", label: "Formation 5 — Intitulé", type: "text" },
    { key: "form5_org", label: "Formation 5 — Organisme", type: "text" },
    { key: "form5_year", label: "Formation 5 — Année", type: "text" },
    { key: "_heading_valeurs", label: "── Mes valeurs", type: "heading" },
    { key: "val1_title", label: "Valeur 1 — Titre", type: "text" },
    { key: "val1_desc", label: "Valeur 1 — Description", type: "textarea" },
    { key: "val2_title", label: "Valeur 2 — Titre", type: "text" },
    { key: "val2_desc", label: "Valeur 2 — Description", type: "textarea" },
    { key: "val3_title", label: "Valeur 3 — Titre", type: "text" },
    { key: "val3_desc", label: "Valeur 3 — Description", type: "textarea" },
    { key: "values_title", label: "Titre section valeurs", type: "text" },
    { key: "values_cta_label", label: "Bouton section valeurs", type: "text" },
  ],
  accompagnements: [
    { key: "_heading_header", label: "── En-tête de page", type: "heading" },
    { key: "page_kicker", label: "Sur-titre", type: "text" },
    { key: "page_title", label: "Titre de la page", type: "text" },
    { key: "intro_text", label: "Texte d'introduction", type: "textarea" },
    { key: "header_image", label: "Photo d'en-tête optionnelle", type: "image" },
    { key: "header_image_alt", label: "Texte alternatif photo d'en-tête", type: "text" },
    { key: "_heading_cards", label: "── Cartes d'accompagnement", type: "heading" },
    ...[1, 2, 3, 4, 5, 6, 7, 8].flatMap(n => [
      { key: `item${n}_title`, label: `Carte ${n} — Titre`, type: "text" },
      { key: `item${n}_desc`, label: `Carte ${n} — Description`, type: "textarea", rows: 3 },
      { key: `item${n}_tags`, label: `Carte ${n} — Tags, un par ligne`, type: "textarea", rows: 3 },
      { key: `item${n}_image`, label: `Carte ${n} — Photo optionnelle`, type: "image" },
      { key: `item${n}_image_alt`, label: `Carte ${n} — Alt photo`, type: "text" },
    ]),
    { key: "_heading_cta", label: "── CTA bas de page", type: "heading" },
    { key: "cta_title", label: "Titre CTA", type: "text" },
    { key: "cta_text", label: "Texte CTA", type: "textarea" },
    { key: "cta_primary_label", label: "Bouton principal", type: "text" },
    { key: "cta_secondary_label", label: "Bouton secondaire", type: "text" },
  ],
  "seances-tarifs": [
    { key: "_heading_intro", label: "── En-tête de page", type: "heading" },
    { key: "page_kicker", label: "Sur-titre", type: "text" },
    { key: "page_title", label: "Titre de la page", type: "text" },
    { key: "intro_text", label: "Texte d'introduction", type: "textarea" },
    { key: "reserve_label", label: "Libellé bouton réserver", type: "text" },

    { key: "_heading_t1", label: "── Tarif 1", type: "heading" },
    { key: "t1_title", label: "Titre", type: "text" },
    { key: "t1_price", label: "Prix (ex: 80€)", type: "text" },
    { key: "t1_duration", label: "Durée (ex: 1h15 — 1h30)", type: "text" },
    { key: "t1_desc", label: "Description courte", type: "textarea" },
    { key: "t1_features", label: "Puces — une par ligne", type: "textarea", rows: 5 },

    { key: "_heading_t2", label: "── Tarif 2 (mis en avant)", type: "heading" },
    { key: "t2_title", label: "Titre", type: "text" },
    { key: "t2_price", label: "Prix", type: "text" },
    { key: "t2_duration", label: "Durée", type: "text" },
    { key: "t2_desc", label: "Description courte", type: "textarea" },
    { key: "t2_features", label: "Puces — une par ligne", type: "textarea", rows: 5 },

    { key: "_heading_t3", label: "── Tarif 3", type: "heading" },
    { key: "t3_title", label: "Titre", type: "text" },
    { key: "t3_price", label: "Prix", type: "text" },
    { key: "t3_duration", label: "Durée", type: "text" },
    { key: "t3_desc", label: "Description courte", type: "textarea" },
    { key: "t3_features", label: "Puces — une par ligne", type: "textarea", rows: 5 },

    { key: "_heading_t4", label: "── Tarif 4", type: "heading" },
    { key: "t4_title", label: "Titre", type: "text" },
    { key: "t4_price", label: "Prix", type: "text" },
    { key: "t4_duration", label: "Durée", type: "text" },
    { key: "t4_desc", label: "Description courte", type: "textarea" },
    { key: "t4_features", label: "Puces — une par ligne", type: "textarea", rows: 5 },

    { key: "_heading_info", label: "── Informations pratiques", type: "heading" },
    { key: "info_title", label: "Titre informations pratiques", type: "text" },
    { key: "info1_q", label: "Question 1", type: "text" },
    { key: "info1_r", label: "Réponse 1", type: "textarea" },
    { key: "info2_q", label: "Question 2", type: "text" },
    { key: "info2_r", label: "Réponse 2", type: "textarea" },
    { key: "info3_q", label: "Question 3", type: "text" },
    { key: "info3_r", label: "Réponse 3", type: "textarea" },
    { key: "info4_q", label: "Question 4", type: "text" },
    { key: "info4_r", label: "Réponse 4", type: "textarea" },

    { key: "_heading_consult", label: "── Encart consultation offerte", type: "heading" },
    { key: "consult_title", label: "Titre", type: "text" },
    { key: "consult_text", label: "Texte", type: "textarea" },
    { key: "consult_button_label", label: "Bouton", type: "text" },
  ],
  "prendre-rendez-vous": [
    { key: "_heading_header", label: "── En-tête de page", type: "heading" },
    { key: "page_title", label: "Titre de la page", type: "text" },
    { key: "page_subtitle", label: "Sous-titre", type: "textarea" },
    { key: "header_image", label: "Photo d'ambiance optionnelle", type: "image" },
    { key: "header_image_alt", label: "Texte alternatif photo", type: "text" },
    { key: "_heading_cards", label: "── Cartes d'information (3 cartes visibles sous le titre)", type: "heading" },
    { key: "info1_title", label: "Carte 1 — Titre (ex: En cabinet)", type: "text" },
    { key: "info1_desc", label: "Carte 1 — Description (ex: Vanves, 92170)", type: "text" },
    { key: "info2_title", label: "Carte 2 — Titre (ex: En ligne)", type: "text" },
    { key: "info2_desc", label: "Carte 2 — Description (ex: Visioconférence...)", type: "text" },
    { key: "info3_title", label: "Carte 3 — Titre (ex: Première séance)", type: "text" },
    { key: "info3_desc", label: "Carte 3 — Description (ex: 1h15 — 80€ | Suivi...)", type: "text" },
    { key: "footer_note", label: "Note en bas de page (ex: Vous ne trouvez pas...)", type: "text" },
  ],
  blog: [
    { key: "_heading_header", label: "── En-tête de page", type: "heading" },
    { key: "page_kicker", label: "Sur-titre", type: "text" },
    { key: "page_title", label: "Titre de la page", type: "text" },
    { key: "intro_text", label: "Texte d'introduction", type: "textarea" },
    { key: "header_image", label: "Photo d'en-tête optionnelle", type: "image" },
    { key: "header_image_alt", label: "Texte alternatif photo", type: "text" },
    { key: "_heading_cards", label: "── Liste des articles", type: "heading" },
    { key: "empty_text", label: "Message si aucun article", type: "text" },
    { key: "read_more_label", label: "Libellé Lire la suite", type: "text" },
    { key: "_heading_article_cta", label: "── CTA article", type: "heading" },
    { key: "article_cta_title", label: "Titre CTA article", type: "text" },
    { key: "article_cta_text", label: "Texte CTA article", type: "textarea" },
    { key: "article_cta_label", label: "Bouton CTA article", type: "text" },
  ],
  contact: [
    { key: "_heading_header", label: "── En-tête de page", type: "heading" },
    { key: "page_kicker", label: "Sur-titre", type: "text" },
    { key: "page_title", label: "Titre de la page", type: "text" },
    { key: "intro_text", label: "Texte d'introduction", type: "textarea" },
    { key: "header_image", label: "Photo d'ambiance optionnelle", type: "image" },
    { key: "header_image_alt", label: "Texte alternatif photo", type: "text" },
    { key: "_heading_info", label: "── Informations de contact", type: "heading" },
    { key: "contact_title", label: "Titre colonne contact", type: "text" },
    { key: "discovery_title", label: "Titre encart découverte", type: "text" },
    { key: "discovery_text", label: "Texte encart découverte", type: "textarea" },
    { key: "_heading_form", label: "── Formulaire", type: "heading" },
    { key: "name_label", label: "Label nom", type: "text" },
    { key: "email_label", label: "Label email", type: "text" },
    { key: "phone_label", label: "Label téléphone", type: "text" },
    { key: "subject_label", label: "Label sujet", type: "text" },
    { key: "message_label", label: "Label message", type: "text" },
    { key: "submit_label", label: "Bouton envoyer", type: "text" },
    { key: "success_title", label: "Titre succès", type: "text" },
    { key: "success_text", label: "Texte succès", type: "textarea" },
  ],
};

PAGE_FIELDS["methode-coaching"] = PAGE_FIELDS["sophrologie-caycedienne"];
PAGE_FIELDS["methode-zenitude"] = PAGE_FIELDS["sophrologie-caycedienne"];

export default function GestionPages() {
  const [pages, setPages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState({});
  const [seo, setSeo] = useState({ seo_title: "", seo_description: "" });
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => axios.get(`${API}/admin/pages`).then(r => {
    const nextPages = Array.isArray(r.data) ? r.data : [];
    setPages(nextPages);
    setSelected(current => current ? nextPages.find(page => page.plan_page_id === current.plan_page_id) || null : null);
  }).catch(() => {});

  useEffect(() => {
    load();
    axios.get(`${API}/admin/media`).then(r => setMedia(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const selectPage = (page) => {
    setSelected(page);
    setContent(page.content || {});
    setSeo({ seo_title: page.seo_title || "", seo_description: page.seo_description || "" });
  };

  const handleSave = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      await axios.put(`${API}/admin/pages/${selected.id}`, {
        title: selected.title,
        slug: selected.slug,
        content,
        seo_title: seo.seo_title,
        seo_description: seo.seo_description,
        status: selected.status || "published",
      });
      announceContentSaved();
      toast.success("Page mise à jour !");
      load();
    } catch { toast.error("Erreur de sauvegarde"); } finally { setLoading(false); }
  };

  const declaredFields = selected?.editor_schema || [];
  const legacyFields = selected ? (PAGE_FIELDS[selected.slug] || []) : [];
  const declaredKeys = new Set(declaredFields.map(field => field.key));
  const legacyKeys = new Set(legacyFields.filter(field => field.type !== "heading").map(field => field.key));
  const isSophrologyPlan = selected?.model_init_id === "sophro-v1@1";
  const fields = !selected ? [] : selected.editor_schema_source === "model" || !isSophrologyPlan ? declaredFields : [
    ...legacyFields.filter(field => field.type === "heading" || declaredKeys.has(field.key)),
    ...declaredFields.filter(field => !legacyKeys.has(field.key)),
  ];
  const previewPath = selected?.route || (selected?.slug === "accueil" ? "/" : selected ? `/${selected.slug}` : "/");
  const dirty = Boolean(selected) && (JSON.stringify(content) !== JSON.stringify(selected.content || {}) || seo.seo_title !== (selected.seo_title || "") || seo.seo_description !== (selected.seo_description || ""));
  const activePlan = pages[0];
  // Display hierarchy only: keep the Plan and its navigation order untouched.
  const pageNodes = pages.map((page, index) => ({
    page, index, route: (page.route || `/${page.slug}`).replace(/\/+$/, "") || "/", children: [],
  }));
  const pageRoots = [];
  pageNodes.forEach(node => {
    const parent = pageNodes.filter(candidate => candidate.route !== "/" && node.route.startsWith(`${candidate.route}/`))
      .sort((a, b) => b.route.length - a.route.length)[0];
    (parent ? parent.children : pageRoots).push(node);
  });
  const renderPageTree = nodes => <ul className="m-0 list-none pl-6">
    {nodes.map((node, position) => {
      const pageData = node.page;
      const isSelected = selected && (selected.plan_page_id ? selected.plan_page_id === pageData.plan_page_id : selected.id === pageData.id);
      return <li key={pageData.plan_page_id || pageData.id || node.index} className="relative min-w-0">
        <span aria-hidden="true" className={`pointer-events-none absolute -left-3 top-0 border-l border-sage/30 ${position === nodes.length - 1 ? "h-7" : "bottom-0"}`}/>
        <span aria-hidden="true" className="pointer-events-none absolute -left-3 top-7 w-3 border-t border-sage/30"/>
        <button type="button" onClick={() => selectPage(pageData)} data-testid={`page-select-${node.index}`} aria-current={isSelected ? "true" : undefined}
          className={`relative flex min-h-14 w-full items-center justify-between gap-2 rounded-lg px-3 py-3 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-sage ${pageData.enabled === false ? "opacity-55" : ""} ${isSelected ? "bg-sage/10 text-sage" : "hover:bg-bone/30 text-charcoal"}`}>
          <div className="min-w-0 flex-1">
            <p className="break-words font-manrope text-sm font-medium">{pageData.plan_page_label || pageData.title}</p>
            <p className="break-all font-manrope text-xs text-charcoal-muted">{pageData.route || `/${pageData.slug}`}{pageData.enabled === false ? " · masquée" : ""}</p>
          </div>
          <ChevronRight size={14} className="shrink-0" aria-hidden="true"/>
        </button>
        {node.children.length > 0 && renderPageTree(node.children)}
      </li>;
    })}
  </ul>;

  return (
    <ContentPublicPreview url={previewPath} title={selected ? `Page · ${selected.title}` : "Page d’accueil"} status={selected?.status === "draft" ? "draft" : "published"} dirty={dirty} action={selected?<button onClick={handleSave} disabled={loading} data-testid="page-save-btn" className="btn-primary !px-5 !py-2.5 text-sm disabled:opacity-60">{loading?<span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"/>:<><Save className="inline" size={16}/> Enregistrer</>}</button>:null}>
    <div className="space-y-6">
      <div>
        <h1 className="font-playfair text-3xl text-charcoal">Arborescence</h1>
        <p className="font-manrope text-sm text-charcoal-muted">Modifiez les pages et les champs prévus par le modèle actuellement appliqué.</p>
        {activePlan && <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl border border-sage/15 bg-white px-4 py-3 font-manrope text-xs">
          <span className="flex items-center gap-2 font-semibold text-charcoal"><Blocks size={15} className="text-sage"/> Plan actif : {activePlan.plan_name}</span>
          <span className="rounded-full bg-bone px-2.5 py-1 text-[10px] uppercase tracking-wider text-charcoal-muted">{activePlan.model_init_id}</span>
          <Link to="/admin/plans" className="ml-auto inline-flex items-center gap-1 font-semibold text-sage hover:underline">Changer de modèle <ExternalLink size={12}/></Link>
        </div>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Page list */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-sage/10 overflow-hidden py-2 pr-2" aria-label="Arborescence des pages">
            {renderPageTree(pageRoots)}
          </div>
        </div>

        {/* Editor */}
        <div className="lg:col-span-3">
          {!selected ? (
            <div className="bg-white rounded-2xl border border-sage/10 py-16 text-center font-manrope text-charcoal-muted">
              Sélectionnez une page à modifier
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-sage/10 p-6 space-y-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div><h2 className="font-playfair text-xl text-charcoal">Modifier : {selected.plan_page_label || selected.title}</h2><p className="mt-1 font-manrope text-xs text-charcoal-muted">Gabarit : {selected.template || "standard"}</p></div>
                <span className="rounded-full border border-sage/15 bg-bone-light px-3 py-1 font-manrope text-[10px] text-charcoal-muted">{selected.modules?.length || 0} module{selected.modules?.length > 1 ? "s" : ""}</span>
              </div>

              {selected.modules?.length > 0 && <div className="flex flex-wrap gap-2 border-y border-sage/10 py-3" aria-label="Modules de la page">
                {selected.modules.map(module => <Link key={module.id} to={`/admin/blocs?type=${encodeURIComponent(module.type)}`} title={`Voir la définition ${module.type}`} className="rounded-lg bg-sage/10 px-2.5 py-1.5 font-manrope text-[11px] text-charcoal transition-colors hover:bg-sage hover:text-white">{module.label}</Link>)}
              </div>}

              {fields.length > 0 ? (
                fields.map(f => {
                  if (f.type === "heading") return (
                    <div key={f.key} className="pt-3 pb-1">
                      <p className="font-manrope font-semibold text-xs text-sage uppercase tracking-widest border-b border-sage/20 pb-2">{f.label.replace("── ", "")}</p>
                      {f.key.startsWith("_heading_t") && f.key !== "_heading_info" && f.key !== "_heading_consult" && f.key !== "_heading_intro" && (
                        <p className="font-manrope text-xs text-charcoal-muted mt-1">Pour les <strong>Puces</strong> : écrivez une puce par ligne</p>
                      )}
                    </div>
                  );
                  return (
                    <div key={f.key}>
                      <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">{f.label}</label>
                      {f.type === "textarea" ? (
                        <textarea value={content[f.key] || ""} onChange={e => setContent(c => ({ ...c, [f.key]: e.target.value }))} rows={f.rows || 3}
                          className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none resize-none transition-all" />
                      ) : f.type === "image" ? (
                        <div className="space-y-3">
                          <div className="flex flex-col md:flex-row gap-3">
                            <input value={content[f.key] || ""} onChange={e => setContent(c => ({ ...c, [f.key]: e.target.value }))}
                              placeholder="Choisissez une image dans la médiathèque"
                              className="flex-1 border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none transition-all" />
                            <select value="" onChange={e => e.target.value && setContent(c => ({ ...c, [f.key]: e.target.value }))}
                              className="md:w-64 border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none transition-all bg-white">
                              <option value="">Choisir dans la médiathèque</option>
                              {media.map(m => <option key={m.id || m.url} value={m.url}>{m.title || m.url}</option>)}
                            </select>
                          </div>
                          {content[f.key] ? (
                            <div className="flex items-start gap-3 rounded-xl border border-sage/10 bg-bone/40 p-3">
                              <img src={content[f.key]} alt="" className="h-24 w-36 rounded-lg object-cover bg-white" onError={e => { e.currentTarget.style.display = "none"; }} />
                              <div className="font-manrope text-xs text-charcoal-muted">
                                <div className="flex items-center gap-1 text-charcoal mb-1"><ImageIcon size={14} /> Aperçu</div>
                                <p className="break-all">{content[f.key]}</p>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <input value={content[f.key] || ""} onChange={e => setContent(c => ({ ...c, [f.key]: e.target.value }))}
                          className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none transition-all" />
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="bg-bone/50 rounded-xl p-4">
                  <p className="font-manrope text-sm text-charcoal-muted">Éditeur JSON du contenu :</p>
                  <textarea value={JSON.stringify(content, null, 2)} onChange={e => { try { setContent(JSON.parse(e.target.value)); } catch {} }}
                    rows={10} className="w-full mt-2 border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-mono text-xs outline-none resize-y transition-all" />
                </div>
              )}

              {/* SEO */}
              <div className="border-t border-sage/10 pt-5 space-y-4">
                <h3 className="font-manrope font-medium text-sm text-charcoal">SEO</h3>
                <div>
                  <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Title SEO</label>
                  <input value={seo.seo_title} onChange={e => setSeo(s => ({ ...s, seo_title: e.target.value }))} placeholder="Titre pour les moteurs de recherche"
                    className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none transition-all" />
                </div>
                <div>
                  <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Meta description</label>
                  <textarea value={seo.seo_description} onChange={e => setSeo(s => ({ ...s, seo_description: e.target.value }))} rows={2}
                    placeholder="Description pour les moteurs de recherche (150-160 caractères)"
                    className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none resize-none transition-all" />
                  <p className="font-manrope text-xs text-charcoal-soft mt-1">{seo.seo_description.length}/160 caractères</p>
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
    </ContentPublicPreview>
  );
}
